// "Create new farming unit" / "Edit farming unit" modal (New Season, flag-gated).
//
// Reads come only from GET v2/distributions (existing values, distribution name,
// and the new unit's id after a create). Writes go through the existing V1
// actions (distributionActions.create_farming_unit / update_farming_unit).
const React = require('react');
const { useState, useEffect, useRef, useCallback } = require('react');
const { useDispatch, useSelector } = require('react-redux');
const { useTranslation } = require('react-i18next');
const distributionActions = require('distributionActions');
const { Modal } = require('./NewSeasonUi');
const { COUNTRY_REGIONS } = require('./newSeasonConstants');
const { load_new_season_distributions } = require('@actions/newSeasonActions');
const { findV2Distribution, farmingUnitIdsOf } = require('./v2Distributions');
const { useV1FarmingUnitWriteStatus } = require('./v1WriteStatus');

// After the V1 create succeeds, re-fetch v2/distributions until the new id
// shows up (the create response carries no id through the V1 action).
const CREATED_ID_ATTEMPTS = 5;
const CREATED_ID_RETRY_MS = 1000;

const EMPTY_FORM = { name: '', mondayID: '', country: '', region: '' };

const formFromFarmingUnit = (farmingUnit) => ({
  name: farmingUnit?.name || '',
  mondayID: farmingUnit?.salesforce_id || '',
  country: farmingUnit?.country || '',
  region: farmingUnit?.region || '',
});

const isFormComplete = (form) =>
  Boolean(
    form.name.trim() && form.mondayID.trim() && form.country && form.region,
  );

const trackMixpanel = (event, properties) => {
  if (typeof mixpanel !== 'undefined') {
    mixpanel.track(event, properties);
  }
};

// V1 update payload with only the basic info. `sensors` is deliberately absent:
// V1's update replaces the unit's sensors only when `sensors` is sent, and the
// V2 farming unit carries none, so sending {} would deactivate them all.
const buildUpdatedFarmingUnit = (farmingUnit, form) => ({
  id: farmingUnit.id,
  name: form.name,
  mondayID: form.mondayID,
  country: form.country,
  region: form.region,
});

const FormRow = ({ label, children }) => (
  <div className='ns-form-row'>
    <label className='ns-form-label'>{label}</label>
    <div className='ns-form-input'>{children}</div>
  </div>
);

const SelectInput = ({ value, options, onChange, placeholder, disabled }) => (
  <select
    className='form-control custom-select ns-input'
    value={value}
    disabled={disabled}
    onChange={(event) => onChange(event.target.value)}
  >
    <option value=''>{placeholder}</option>
    {options.map((option) => (
      <option key={option} value={option}>
        {option}
      </option>
    ))}
  </select>
);

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const findNewFarmingUnitId = async (dispatch, distributionId, knownIds) => {
  for (let attempt = 0; attempt < CREATED_ID_ATTEMPTS; attempt += 1) {
    const distributions = await dispatch(load_new_season_distributions());
    const newId = farmingUnitIdsOf(distributions, distributionId).find(
      (id) => !knownIds.has(id),
    );
    if (newId) {
      return newId;
    }
    await wait(CREATED_ID_RETRY_MS);
  }
  return null;
};

const FarmingUnitFormModal = ({
  isOpen,
  mode,
  distributionId,
  farmingUnit,
  onClose,
  onCreated,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const farmingUnitActions = useV1FarmingUnitWriteStatus();
  const distributions = useSelector(
    (state) => state.new_season_data?.distributions,
  );
  const distributionName = findV2Distribution(
    distributions,
    distributionId,
  )?.name;
  const knownIdsRef = useRef(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const isCreate = mode === 'create';

  useEffect(() => {
    if (isOpen) {
      setForm(isCreate ? EMPTY_FORM : formFromFarmingUnit(farmingUnit));
      setPending(false);
      setError('');
    }
  }, [isOpen, isCreate, farmingUnit]);

  const onCreateSucceeded = useCallback(() => {
    findNewFarmingUnitId(dispatch, distributionId, knownIdsRef.current).then(
      (newId) => {
        setPending(false);
        if (newId) {
          onCreated(newId);
        } else {
          setError(t('new_season_created_not_found'));
        }
      },
    );
  }, [dispatch, distributionId, onCreated, t]);

  useEffect(() => {
    if (!pending || !farmingUnitActions) {
      return;
    }
    const expectedType = isCreate ? 'CREATE' : 'UPDATE';
    if (farmingUnitActions.type !== expectedType) {
      return;
    }
    if (farmingUnitActions.status === 'fail') {
      setPending(false);
      setError(farmingUnitActions.error || '');
      dispatch(distributionActions.clear_farming_unit_action());
    } else if (farmingUnitActions.status === 'success') {
      dispatch(distributionActions.clear_farming_unit_action());
      if (isCreate) {
        onCreateSucceeded();
      } else {
        setPending(false);
        dispatch(load_new_season_distributions());
        onClose();
      }
    }
  }, [
    farmingUnitActions,
    pending,
    isCreate,
    dispatch,
    onClose,
    onCreateSucceeded,
  ]);

  const updateForm = (key, value) =>
    setForm((previous) => ({
      ...previous,
      [key]: value,
      ...(key === 'country' ? { region: '' } : {}),
    }));

  const onSubmit = (event) => {
    event.preventDefault();
    if (!isFormComplete(form) || pending) {
      return;
    }
    setError('');
    setPending(true);
    if (isCreate) {
      // Snapshot the distribution's farming units (V2) before creating.
      Promise.resolve(dispatch(load_new_season_distributions())).then(
        (latest) => {
          knownIdsRef.current = new Set(
            farmingUnitIdsOf(latest || distributions, distributionId),
          );
          dispatch(
            distributionActions.create_farming_unit(distributionId, {
              ...form,
              sensors: {},
            }),
          );
          trackMixpanel('Farming Unit Created', {
            Distribution: distributionName,
            'Farming Unit': form.name,
          });
        },
      );
    } else {
      dispatch(
        distributionActions.update_farming_unit(
          distributionId,
          buildUpdatedFarmingUnit(farmingUnit, form),
        ),
      );
    }
  };

  const regions = COUNTRY_REGIONS[form.country] || [];
  const canSubmit = isFormComplete(form) && !pending;

  return (
    <Modal isOpen={isOpen} onClose={pending ? () => {} : onClose}>
      <form onSubmit={onSubmit}>
        <div className='ns-modal-header'>
          {isCreate
            ? t('new_season_create_farming_unit')
            : t('new_season_edit_farming_unit')}
        </div>
        <div className='ns-modal-body'>
          <FormRow label={t('new_season_unit_name')}>
            <input
              className='form-control ns-input'
              value={form.name}
              placeholder={t('new_season_enter')}
              onChange={(event) => updateForm('name', event.target.value)}
            />
          </FormRow>
          <FormRow label={t('new_season_monday_id')}>
            <input
              className='form-control ns-input'
              value={form.mondayID}
              placeholder={t('new_season_enter')}
              onChange={(event) => updateForm('mondayID', event.target.value)}
            />
          </FormRow>
          <FormRow label={t('country')}>
            <SelectInput
              value={form.country}
              options={Object.keys(COUNTRY_REGIONS)}
              placeholder={t('new_season_select')}
              onChange={(value) => updateForm('country', value)}
            />
          </FormRow>
          <FormRow label={t('region')}>
            <SelectInput
              value={form.region}
              options={regions}
              placeholder={t('new_season_select')}
              disabled={!form.country}
              onChange={(value) => updateForm('region', value)}
            />
          </FormRow>
          <div className='ns-collapsed-row ns-disabled' aria-disabled='true'>
            <span>› {t('new_season_unit_sensors')}</span>
            <span>+</span>
          </div>
          {error && <div className='ns-error-text'>{error}</div>}
        </div>
        <div className='ns-modal-footer'>
          <button
            type='button'
            className='ns-btn ns-btn-outline'
            onClick={onClose}
            disabled={pending}
          >
            {t('cancel')}
          </button>
          <button
            type='submit'
            className='ns-btn ns-btn-primary'
            disabled={!canSubmit}
          >
            {isCreate ? t('next') : t('save')}
            {pending && (
              <span
                className='spinner-border spinner-border-sm ml-2'
                role='status'
              />
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

module.exports = FarmingUnitFormModal;
